import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { Map, Target, Shield, FileSignature, Eye, Brain, BarChart3, Clock, Flame, CheckCircle, Check } from 'lucide-react';
import { trackAddToCart, trackInitiateCheckout } from '../utils/trackAddToCart';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-10% 0px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 30
  }} transition={{
    duration: 0.6,
    delay,
    ease: 'easeOut'
  }} data-id="element-9">
      {children}
    </motion.div>;
};
export function BonusesSection() {
  return <section className="w-full bg-[#050505] py-24 px-4 relative overflow-hidden" data-id="element-10">
      {/* Background Gradient */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505] pointer-events-none" data-id="element-11"></div>

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-12">
        {/* SECTION HEADER */}
        <div className="text-center mb-20 max-w-3xl mx-auto" data-id="element-13">
          <FadeIn data-id="element-14">
            <h2 className="font-serif text-3xl md:text-5xl text-[#f5f0e8] mb-6" data-id="element-15">
              Bonos (Valor{' '}
              <span className="line-through decoration-red-500/50 text-[#8a8578]" data-id="element-16">
                $47 USD
              </span>
              ) — <span className="text-[#d4af37]" data-id="element-17">Gratis Hoy</span>
            </h2>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-18">
            <p className="text-[#8a8578] text-lg leading-relaxed mb-8" data-id="element-19">
              Cuando obtienes Trading en Paz, no solo recibes el sistema mental
              (PP-7 + RI-3). Para que lo implementes sin fricción y empieces a
              notar control desde la primera sesión, hoy te incluyo 3 bonos
              diseñados para convertir disciplina en algo ejecutable.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-20">
            <div className="inline-block px-6 py-3 bg-[#0a0a0a] border border-[#222] rounded-full" data-id="element-21">
              <p className="text-sm md:text-base text-[#8a8578]" data-id="element-22">
                Valor total de los bonos:{' '}
                <span className="line-through decoration-red-500/50" data-id="element-23">
                  $47 USD
                </span>{' '}
                <span className="mx-2" data-id="element-24">|</span> Tu precio hoy:{' '}
                <span className="text-[#d4af37] font-bold" data-id="element-25">$0 (gratis)</span>
              </p>
            </div>
          </FadeIn>
        </div>

        {/* BONUS CARDS */}
        <div className="space-y-12 mb-24" data-id="element-26">
          {/* BONO #1 */}
          <FadeIn data-id="element-27">
            <div className="bg-[#0a0a0a] border border-[#222] rounded-xl overflow-hidden flex flex-col md:flex-row group hover:border-[#d4af37]/30 transition-colors duration-500" data-id="element-28">
              <div className="md:w-2/5 relative min-h-[300px] md:min-h-full bg-[#080808] flex items-center justify-center p-8" data-id="element-29">
                <div className="absolute inset-0 bg-[#d4af37]/5 blur-[40px] rounded-full" data-id="element-30"></div>
                <img src="https://cdn.magicpatterns.com/uploads/qaQvkXWJZ8RDR5y9YY3mdE/Bono_1_Imagen.png" alt="Anti-Autosabotaje" className="w-full h-auto rounded-lg border border-[#222]" loading="lazy" decoding="async" data-id="element-31" />
              </div>
              <div className="md:w-3/5 p-8 md:p-10 flex flex-col" data-id="element-32">
                <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-33">
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-xs font-bold tracking-wider" data-id="element-34">
                    BONO #1 (GRATIS)
                  </span>
                  <span className="text-xs text-[#8a8578]" data-id="element-35">
                    Valor: <span className="line-through" data-id="element-36">$17 USD</span> |{' '}
                    <span className="text-[#d4af37]" data-id="element-37">Hoy: GRATIS</span>
                  </span>
                </div>

                <h3 className="font-serif text-2xl text-[#f5f0e8] mb-4" data-id="element-38">
                  Anti-Autosabotaje: Mapa de Gatillos + Plan de Intervención
                </h3>

                <p className="text-[#8a8578] text-sm mb-4" data-id="element-39">
                  Este es el bono para el momento donde más te traicionas:
                  cuando tu mente empieza a negociar con la misma frase de
                  siempre…
                </p>

                <div className="pl-4 border-l-2 border-[#d4af37]/30 mb-6 italic text-[#d4af37]/80 text-sm space-y-1" data-id="element-40">
                  <p data-id="element-41">"Solo un trade más."</p>
                  <p data-id="element-42">"Solo para recuperar."</p>
                  <p data-id="element-43">"Solo porque está clarísimo."</p>
                </div>

                <p className="text-[#f5f0e8] font-medium mb-4" data-id="element-44">
                  Aquí no hay teoría. Hay intervención.
                </p>

                <div className="space-y-4 mb-6" data-id="element-45">
                  <h4 className="text-xs uppercase tracking-widest text-[#555]" data-id="element-46">
                    Qué te da:
                  </h4>
                  <ul className="space-y-3" data-id="element-47">
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-48">
                      <Map className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-49" />
                      <span data-id="element-50">
                        Un mapa visual de los 7 gatillos que te cuestan dinero y
                        paz (FOMO, venganza, euforia, aburrimiento, vergüenza,
                        comparación, fatiga)
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-51">
                      <Target className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-52" />
                      <span data-id="element-53">
                        Un plan para identificar tu gatillo dominante del día en
                        90 segundos
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-54">
                      <Shield className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-55" />
                      <span data-id="element-56">
                        La respuesta mecánica para cortar el impulso antes del
                        pico (sin debatir contigo)
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="mt-auto pt-6 border-t border-[#222]" data-id="element-57">
                  <p className="text-sm italic text-[#d4af37]" data-id="element-58">
                    <span className="font-bold not-italic text-[#555] mr-2" data-id="element-59">
                      Por qué importa:
                    </span>
                    "Porque el autosabotaje no se vence con ganas. Se vence con
                    un protocolo que se activa solo."
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* BONO #2 */}
          <FadeIn data-id="element-60">
            <div className="bg-[#0a0a0a] border border-[#222] rounded-xl overflow-hidden flex flex-col md:flex-row group hover:border-[#d4af37]/30 transition-colors duration-500" data-id="element-61">
              <div className="md:w-2/5 relative min-h-[300px] md:min-h-full bg-[#080808] flex items-center justify-center p-8" data-id="element-62">
                <div className="absolute inset-0 bg-[#d4af37]/5 blur-[40px] rounded-full" data-id="element-63"></div>
                <img src="https://cdn.magicpatterns.com/uploads/ooH1SwCackC2np9RuWDeDK/Bono_2.png" alt="Contrato del Ejecutor" className="w-full h-auto rounded-lg border border-[#222]" loading="lazy" decoding="async" data-id="element-64" />
              </div>
              <div className="md:w-3/5 p-8 md:p-10 flex flex-col" data-id="element-65">
                <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-66">
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-xs font-bold tracking-wider" data-id="element-67">
                    BONO #2 (GRATIS)
                  </span>
                  <span className="text-xs text-[#8a8578]" data-id="element-68">
                    Valor: <span className="line-through" data-id="element-69">$13 USD</span> |{' '}
                    <span className="text-[#d4af37]" data-id="element-70">Hoy: GRATIS</span>
                  </span>
                </div>

                <h3 className="font-serif text-2xl text-[#f5f0e8] mb-4" data-id="element-71">
                  Identidad Trader Rentable: El Contrato del Ejecutor (RI-3 en
                  14 días)
                </h3>

                <p className="text-[#8a8578] text-sm mb-4" data-id="element-72">
                  Este bono es confrontativo, porque es lo que casi nadie quiere
                  aceptar:
                </p>

                <div className="pl-4 border-l-2 border-[#d4af37]/30 mb-6 italic text-[#d4af37]/80 text-sm" data-id="element-73">
                  <p data-id="element-74">
                    "Si no está escrito, se negocia. Si se negocia, se rompe. Y
                    si se rompe, tu identidad se parte en dos."
                  </p>
                </div>

                <div className="space-y-4 mb-6" data-id="element-75">
                  <h4 className="text-xs uppercase tracking-widest text-[#555]" data-id="element-76">
                    Qué te da:
                  </h4>
                  <ul className="space-y-3" data-id="element-77">
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-78">
                      <FileSignature className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-79" />
                      <span data-id="element-80">
                        Un Contrato del Ejecutor (firmable) con reglas no
                        negociables para operar como profesional
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-81">
                      <Eye className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-82" />
                      <span data-id="element-83">
                        Enfoque brutal en ambiente (lo que disparas con tu
                        setup, redes, notificaciones, fatiga)
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-84">
                      <Brain className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-85" />
                      <span data-id="element-86">
                        El ciclo que te opera en cada sesión: Ambiente →
                        Pensamiento → Sentimiento → Acción → Resultado
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-87">
                      <BarChart3 className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-88" />
                      <span data-id="element-89">
                        Un sistema de evidencia diaria (60 segundos) que instala
                        identidad sin depender de motivación
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="mt-auto pt-6 border-t border-[#222]" data-id="element-90">
                  <p className="text-sm italic text-[#d4af37]" data-id="element-91">
                    <span className="font-bold not-italic text-[#555] mr-2" data-id="element-92">
                      Por qué importa:
                    </span>
                    "Porque el trader rentable no es el que sabe más. Es el que
                    ejecuta igual cuando gana, cuando pierde y cuando el ego
                    aprieta."
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* BONO #3 */}
          <FadeIn data-id="element-93">
            <div className="bg-[#0a0a0a] border border-[#222] rounded-xl overflow-hidden flex flex-col md:flex-row group hover:border-[#d4af37]/30 transition-colors duration-500" data-id="element-94">
              <div className="md:w-2/5 relative min-h-[300px] md:min-h-full bg-[#080808] flex items-center justify-center p-8" data-id="element-95">
                <div className="absolute inset-0 bg-[#d4af37]/5 blur-[40px] rounded-full" data-id="element-96"></div>
                <img src="https://cdn.magicpatterns.com/uploads/cpYUmPGveHC3u7DvfiMsDs/Bono_3.png" alt="Rutina PDF" className="w-full h-auto rounded-lg border border-[#222]" loading="lazy" decoding="async" data-id="element-97" />
              </div>
              <div className="md:w-3/5 p-8 md:p-10 flex flex-col" data-id="element-98">
                <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-99">
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-xs font-bold tracking-wider" data-id="element-100">
                    BONO #3 (GRATIS)
                  </span>
                  <span className="text-xs text-[#8a8578]" data-id="element-101">
                    Valor: <span className="line-through" data-id="element-102">$51.000 COP</span> |{' '}
                    <span className="text-[#d4af37]" data-id="element-103">Hoy: GRATIS</span>
                  </span>
                </div>

                <h3 className="font-serif text-2xl text-[#f5f0e8] mb-4" data-id="element-104">
                  Rutina PDF: Inicio + En Caliente + Cierre de Sesión (3 minutos
                  para operar plano)
                </h3>

                <p className="text-[#8a8578] text-sm mb-4" data-id="element-105">
                  Si tu sesión empieza sin ritual, empieza con improvisación. Y
                  si termina sin cierre, termina con espiral.
                </p>

                <p className="text-[#f5f0e8] font-medium mb-4" data-id="element-106">
                  Este bono te entrega una rutina corta tipo SOP (procedimiento
                  profesional) para:
                </p>

                <div className="space-y-4 mb-6" data-id="element-107">
                  <h4 className="text-xs uppercase tracking-widest text-[#555]" data-id="element-108">
                    Qué te da:
                  </h4>
                  <ul className="space-y-3" data-id="element-109">
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-110">
                      <Clock className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-111" />
                      <span data-id="element-112">
                        Inicio en 90 segundos: semáforo + regla del día +
                        checklist
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-113">
                      <Flame className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-114" />
                      <span data-id="element-115">
                        Intervención en caliente en 45 segundos: pausa física +
                        respiración + regla mecánica
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-116">
                      <CheckCircle className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-117" />
                      <span data-id="element-118">
                        Cierre en 90 segundos: evidencia mínima + ajuste para
                        mañana (sin castigo)
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="mt-auto pt-6 border-t border-[#222]" data-id="element-119">
                  <p className="text-sm italic text-[#d4af37]" data-id="element-120">
                    <span className="font-bold not-italic text-[#555] mr-2" data-id="element-121">
                      Por qué importa:
                    </span>
                    "Porque el autosabotaje casi siempre ocurre cuando: entras
                    sin estado o te quedas emocionalmente abierto al cerrar."
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* PACKAGE SUMMARY */}
        <FadeIn data-id="element-122">
          <div className="max-w-2xl mx-auto bg-[#0a0a0a] border border-[#222] border-t-4 border-t-[#d4af37] rounded-xl p-8 mb-16 shadow-2xl" data-id="element-123">
            <h3 className="text-center font-serif text-2xl text-[#f5f0e8] mb-8" data-id="element-124">
              Resumen del paquete
            </h3>
            <ul className="space-y-4 mb-8" data-id="element-125">
              <li className="flex items-start gap-3 text-[#f5f0e8]" data-id="element-126">
                <Check className="w-5 h-5 text-[#d4af37] flex-shrink-0" data-id="element-127" />
                <span data-id="element-128">
                  <span className="font-bold" data-id="element-129">
                    Trading en Paz (PDF principal):
                  </span>{' '}
                  PP-7 + RI-3
                </span>
              </li>
              <li className="flex items-start gap-3 text-[#f5f0e8]" data-id="element-130">
                <Check className="w-5 h-5 text-[#d4af37] flex-shrink-0" data-id="element-131" />
                <span data-id="element-132">
                  <span className="font-bold" data-id="element-133">Bono #1:</span> Anti-Autosabotaje
                  (Mapa + Intervención)
                </span>
              </li>
              <li className="flex items-start gap-3 text-[#f5f0e8]" data-id="element-134">
                <Check className="w-5 h-5 text-[#d4af37] flex-shrink-0" data-id="element-135" />
                <span data-id="element-136">
                  <span className="font-bold" data-id="element-137">Bono #2:</span> Contrato del
                  Ejecutor (Identidad + Ambiente + Leyes)
                </span>
              </li>
              <li className="flex items-start gap-3 text-[#f5f0e8]" data-id="element-138">
                <Check className="w-5 h-5 text-[#d4af37] flex-shrink-0" data-id="element-139" />
                <span data-id="element-140">
                  <span className="font-bold" data-id="element-141">Bono #3:</span> Rutina de 3
                  minutos (Inicio/En caliente/Cierre)
                </span>
              </li>
            </ul>
            <div className="text-center pt-6 border-t border-[#222]" data-id="element-142">
              <p className="text-[#8a8578] text-sm md:text-base" data-id="element-143">
                Valor de bonos:{' '}
                <span className="line-through decoration-red-500/50" data-id="element-144">
                  $47 USD
                </span>{' '}
                <br className="md:hidden" data-id="element-145" />
                <span className="text-[#d4af37] font-bold ml-2" data-id="element-146">
                  Hoy: incluidos GRATIS con tu acceso
                </span>
              </p>
            </div>
          </div>
        </FadeIn>

        {/* BOTTOM CTA */}
        <FadeIn data-id="element-147">
          <div className="text-center max-w-2xl mx-auto" data-id="element-148">
            <p className="text-xl text-[#f5f0e8] mb-2 font-medium" data-id="element-149">
              Si estás cansado de saber qué hacer… y aún así no hacerlo, haz
              esto ahora:
            </p>
            <p className="text-[#8a8578] mb-8" data-id="element-150">
              Descarga Trading en Paz y recibe los 3 bonos gratis para ejecutar
              desde hoy.
            </p>

            <a href="https://pay.hotmart.com/I104339970M?checkoutMode=10" target="_blank" rel="noopener noreferrer" onClick={() => trackInitiateCheckout()} className="inline-block px-12 py-4 bg-gradient-to-r from-[#b8942e] via-[#d4af37] to-[#e8c84a] text-black font-bold text-lg tracking-wide rounded hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all duration-300 transform hover:-translate-y-1" data-id="element-151">
              DESCARGAR AHORA
            </a>
          </div>
        </FadeIn>
      </div>
    </section>;
}